import { Box, Icon, Link, Text } from '@factorialco/deprecated-design-system'
import {
  ProfileEmployeeAccessFragment,
  ProfileEmployeePoliciesFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EmployeeStatusBannerEmployee } from 'modules/employees/components/blocks/EmployeeStatusBanner/types'
import { useSendInvitation } from 'modules/employees/hooks/useSendInvitation'

interface Props {
  access: ProfileEmployeeAccessFragment
  employee: EmployeeStatusBannerEmployee
  policies?: ProfileEmployeePoliciesFragment | null
}

const InvitationNotSentOrAccepted = ({ access, employee, policies }: Props) => {
  const {
    invitationSent,
    invitationSentToUsers,
    performAction: sendInvitation,
  } = useSendInvitation(employee.id)

  const alreadyInvited = access.invitedOn
  const alreadyJoined = access.isConfirmed

  const canInvite = policies?.inviteEmployeeToApp.can ?? false
  const canShareProfileEmail = policies?.shareProfileEmail.can ?? false
  const hasEmail = !!employee.email
  const isNotTerminated = !employee.isTerminating

  const isVisible =
    canShareProfileEmail &&
    hasEmail &&
    canInvite &&
    isNotTerminated &&
    ((alreadyInvited && !alreadyJoined) || (!alreadyInvited && !alreadyJoined))

  if (!isVisible) return null

  const currentlyInvited = invitationSentToUsers.some((id) => employee.id === id)
  const isInvited = access.invitedOn || currentlyInvited
  const invitedAt = currentlyInvited ? moment() : moment(access.invitedOn)

  const title = () => {
    if (isInvited) {
      return i18n.t('employees.creation.invitation_not_accepted.title', {
        employee_name: employee.preferredName || employee.fullName,
      })
    }

    return i18n.t('employees.creation.invitation_not_received', {
      employee_name: employee.preferredName || employee.fullName,
    })
  }

  return (
    <Box paddingBottom="s32" paddingX="s64">
      <Box
        borderRadius={{ all: 'abs016' }}
        background="champagne200"
        padding="s12"
        paddingLeft="s16"
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
      >
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" width="full">
          <Box gap="s12" flexDirection="row" alignItems="center" paddingRight="s64">
            <Box background="champagne1200" borderRadius={{ all: 'abs008' }} padding="s2">
              <Icon.Medium icon="flag" color="grey000White" />
            </Box>
            <Text.Normal weight="medium">{title()}</Text.Normal>
            {isInvited && (
              <Text.Normal weight="medium">
                {i18n.t('employees.creation.invitation_not_accepted.subtitle', {
                  invitation_date: dateToString(invitedAt, 'ui'),
                })}
              </Text.Normal>
            )}
          </Box>

          <Box flexNoShrink>
            <Link.Touchable
              disabled={invitationSent}
              onClick={() => !invitationSent && sendInvitation()}
            >
              {isInvited ? i18n.t('employees.reinvite') : i18n.t('employees.invite')}
            </Link.Touchable>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default observer(InvitationNotSentOrAccepted)
